/**
 * 0.55: Zulässiger Jahresbereich für Termine. Ältere Daten liefert PostgreSQL in Europe/Berlin mit
 * Ortszeit-Versatz in Sekunden („+00:53:28“) – das kann JavaScript nicht lesen und die Karte wäre
 * nicht mehr ladbar. Datumsfelder melden beim Tippen des Jahres außerdem Zwischenwerte wie „0002-…“.
 */
export const DATE_MIN_YEAR = 1900;
export const DATE_MAX_YEAR = 2999;
export const DATE_RANGE_TEXT = `Bitte ein Jahr zwischen ${DATE_MIN_YEAR} und ${DATE_MAX_YEAR} eingeben.`;
/** `YYYY-MM-DD…` mit Jahr im zulässigen Bereich */
export function plausibleDate(value: string): boolean {
  const y = Number(/^(\d{4})-/.exec(value)?.[1]);
  return y >= DATE_MIN_YEAR && y <= DATE_MAX_YEAR;
}

export type DueState = 'none' | 'done' | 'overdue' | 'soon' | 'later';

/*
 * Datumsmodell (R06):
 * - Termin MIT Uhrzeit = Zeitpunkt (UTC-ISO), wird in der Zeitzone des Betrachters angezeigt.
 * - Termin OHNE Uhrzeit (ganztägig, auch jeder Start) = Kalenderdatum. Gespeichert als
 *   „JJJJ-MM-TTT00:00:00.000Z“; maßgeblich ist nur der Datumsteil. Er wird nie in eine
 *   Zeitzone umgerechnet und ist damit für alle im Team derselbe Tag.
 * - Ein ganztägiger Termin gilt bis zum Ende dieses Tages in der Zeitzone des Betrachters;
 *   E-Mail-Erinnerungen des Servers nutzen die Zeitzone der Instanz (TZ).
 */

/** Kalenderdatum „JJJJ-MM-TT“ → gespeicherter Wert für ganztägige Termine */
export function allDayIso(date: string): string {
  return `${date}T00:00:00.000Z`;
}

/** Lokales Datum → „JJJJ-MM-TT“ */
export function localDateKey(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

/** Kalendertag eines Termins: ganztägig = Datumsteil, mit Uhrzeit = lokales Datum des Betrachters */
export function calendarDate(iso: string, hasTime: boolean): string {
  return hasTime ? localDateKey(new Date(iso)) : iso.slice(0, 10);
}

/** Lokaler Date-Wert (Mitternacht) des Kalendertags – zum Anzeigen und Vergleichen */
export function calendarDay(iso: string, hasTime: boolean): Date {
  const [y, m, d] = calendarDate(iso, hasTime).split('-').map(Number);
  return new Date(y!, m! - 1, d!);
}

/** Endzeitpunkt einer Frist für den Betrachter */
export function dueDeadline(iso: string, hasTime: boolean): Date {
  if (hasTime) return new Date(iso);
  const day = calendarDay(iso, false);
  day.setHours(23, 59, 59, 999);
  return day;
}

/** Zustand einer Frist. Datum ohne Uhrzeit gilt bis zum Ende des Kalendertags (lokal beim Betrachter). */
export function dueState(
  dueAt: string | null,
  dueHasTime: boolean,
  completedAt: string | null,
  now: Date = new Date(),
): DueState {
  if (!dueAt) return 'none';
  if (completedAt) return 'done';
  const diff = dueDeadline(dueAt, dueHasTime).getTime() - now.getTime();
  if (diff < 0) return 'overdue';
  if (diff < 24 * 60 * 60 * 1000) return 'soon';
  return 'later';
}

const dateFmt = new Intl.DateTimeFormat('de-DE', { day: 'numeric', month: 'short' });
const dateTimeFmt = new Intl.DateTimeFormat('de-DE', {
  day: 'numeric',
  month: 'short',
  hour: '2-digit',
  minute: '2-digit',
});

export function formatDue(dueAt: string, dueHasTime: boolean): string {
  return dueHasTime ? dateTimeFmt.format(new Date(dueAt)) : dateFmt.format(calendarDay(dueAt, false));
}

/** Aus Datumsfeld (+ optionaler Uhrzeit, lokal) den zu speichernden Wert bilden */
export function fromDateInputs(date: string, time: string | null): string | null {
  if (!date) return null;
  if (!time) return allDayIso(date);
  const [y, m, d] = date.split('-').map(Number);
  const [hh, mm] = time.split(':').map(Number);
  return new Date(y!, m! - 1, d!, hh ?? 0, mm ?? 0).toISOString();
}

/** Termin auf einen anderen Kalendertag legen; Uhrzeit (lokal) bleibt erhalten */
export function moveToDate(iso: string | null, hasTime: boolean, date: string): string {
  if (!iso || !hasTime) return allDayIso(date);
  const old = new Date(iso);
  const [y, m, d] = date.split('-').map(Number);
  return new Date(y!, m! - 1, d!, old.getHours(), old.getMinutes()).toISOString();
}

/*
 * Start mit optionaler Uhrzeit (wie die Frist): ohne Uhrzeit ein Kalenderdatum, mit Uhrzeit ein
 * Zeitpunkt. Der Zeitraum ist gültig, wenn der Start nicht nach der Frist liegt:
 *  - beide mit Uhrzeit: Zeitpunkte vergleichen,
 *  - sonst Kalendertage vergleichen (gleicher Tag ist immer gültig).
 */

/** Beginn eines Starts für den Betrachter (ganztägig = 0:00 Uhr lokal) */
export function startBegin(iso: string, hasTime: boolean): Date {
  return hasTime ? new Date(iso) : calendarDay(iso, false);
}

export function formatStart(startAt: string, startHasTime: boolean): string {
  return formatDue(startAt, startHasTime);
}

/** true, wenn der Start nach der Frist liegt */
export function startAfterDue(
  startAt: string | null | undefined,
  startHasTime: boolean,
  dueAt: string | null | undefined,
  dueHasTime: boolean,
): boolean {
  if (!startAt || !dueAt) return false;
  if (startHasTime && dueHasTime) return new Date(startAt).getTime() > new Date(dueAt).getTime();
  return calendarDate(startAt, startHasTime) > calendarDate(dueAt, dueHasTime);
}

/**
 * Wie startAfterDue, aber ohne Zeitzone des Betrachters (Server): Bei gemischten Terminen gilt der
 * Zeitraum als ungültig nur, wenn er es in jeder Zeitzone (UTC−12 bis UTC+14) wäre.
 */
export function startAfterDueAnyZone(
  startAt: string | null | undefined,
  startHasTime: boolean,
  dueAt: string | null | undefined,
  dueHasTime: boolean,
): boolean {
  if (!startAt || !dueAt) return false;
  if (startHasTime && dueHasTime) return new Date(startAt).getTime() > new Date(dueAt).getTime();
  const H = 3_600_000;
  const startDay = startHasTime ? new Date(new Date(startAt).getTime() - 12 * H).toISOString().slice(0, 10) : startAt.slice(0, 10);
  const dueDay = dueHasTime ? new Date(new Date(dueAt).getTime() + 14 * H).toISOString().slice(0, 10) : dueAt.slice(0, 10);
  return startDay > dueDay;
}

/** Zeitzone des Geräts (Uhrzeiten werden darin eingegeben und angezeigt) */
export function localTimeZone(): string {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || 'Ortszeit';
  } catch {
    return 'Ortszeit';
  }
}

/**
 * Uhrzeit-Eingabe im 24-Stunden-Format lesen: „9“, „930“, „09:30“, „9.30“, „0:00“ → „HH:MM“.
 * Ungültig → null.
 */
export function parseTimeInput(raw: string): string | null {
  const t = raw.trim().replace(/\s+/g, '').replace(/uhr$/i, '').replace(/[.,h]/gi, ':');
  let h: number, m: number;
  const colon = t.match(/^(\d{1,2}):(\d{1,2})$/);
  if (colon) {
    h = Number(colon[1]);
    m = Number(colon[2]);
    if (colon[2]!.length === 1) m *= 10;
  } else if (/^\d{1,2}$/.test(t)) {
    h = Number(t);
    m = 0;
  } else if (/^\d{3,4}$/.test(t)) {
    h = Number(t.slice(0, t.length - 2));
    m = Number(t.slice(-2));
  } else return null;
  if (!(h >= 0 && h <= 23 && m >= 0 && m <= 59)) return null;
  return `${String(h).padStart(2, '0')}:${String(m).padStart(2, '0')}`;
}

/** Lokale Uhrzeit „HH:MM“ eines Zeitpunkts */
export function timeOf(iso: string): string {
  const d = new Date(iso);
  return `${String(d.getHours()).padStart(2, '0')}:${String(d.getMinutes()).padStart(2, '0')}`;
}

/**
 * Frist verschieben und den Start mitnehmen, falls er sonst nach der Frist läge: Der Start rückt um
 * dieselbe Anzahl Kalendertage (Uhrzeit bleibt). Gibt den neuen Start zurück oder undefined (unverändert).
 * Als Änderung zum Mitschicken: `{ startAt, startHasTime }`.
 */
export function shiftStartWithDue(
  card: { startAt: string | null; startHasTime: boolean; dueAt: string | null; dueHasTime: boolean },
  nextDueAt: string | null,
  nextDueHasTime: boolean,
): { startAt: string; startHasTime: boolean } | undefined {
  if (!card.startAt || !nextDueAt) return undefined;
  if (!startAfterDue(card.startAt, card.startHasTime, nextDueAt, nextDueHasTime)) return undefined;
  const from = card.dueAt ? calendarDay(card.dueAt, card.dueHasTime) : calendarDay(card.startAt, card.startHasTime);
  const to = calendarDay(nextDueAt, nextDueHasTime);
  const days = Math.round((to.getTime() - from.getTime()) / 86_400_000);
  const s = calendarDay(card.startAt, card.startHasTime);
  const target = localDateKey(new Date(s.getFullYear(), s.getMonth(), s.getDate() + days));
  const moved = moveToDate(card.startAt, card.startHasTime, target);
  // Immer noch danach (z. B. Uhrzeit am selben Tag)? Dann ganztägig auf den Tag der Frist
  return startAfterDue(moved, card.startHasTime, nextDueAt, nextDueHasTime)
    ? { startAt: allDayIso(calendarDate(nextDueAt, nextDueHasTime)), startHasTime: false }
    : { startAt: moved, startHasTime: card.startHasTime };
}
